// frontend/src/state/credits.js
import React from "react";
import { apiGet } from "../lib/api";

const CreditsContext = React.createContext(null);

export function CreditsProvider({ children }) {
  const [credits, setCredits] = React.useState(0);
  const [loading, setLoading] = React.useState(false);

  const refresh = React.useCallback(async () => {
    setLoading(true);
    try {
      const json = await apiGet("/api/credits");
      const next = Number(json?.credits ?? 0);
      if (Number.isFinite(next)) setCredits(next);
    } catch (e) {
      console.error("credits.refresh failed:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  const spend = React.useCallback((amount = 1) => {
    const n = Number(amount);
    if (!Number.isFinite(n) || n <= 0) return;
    setCredits((prev) => Math.max(0, (Number(prev) || 0) - n));
  }, []);

  // Initial load
  React.useEffect(() => { 
    refresh(); 
  }, [refresh]);

  // Listen to custom events
  React.useEffect(() => {
    const onSet = (e) => {
      const v = Number(e?.detail?.value);
      if (Number.isFinite(v)) setCredits(v);
    };
    const onSpend = (e) => {
      const a = Number(e?.detail?.amount ?? 1);
      if (Number.isFinite(a) && a > 0) spend(a);
    };
    const onRefresh = () => refresh();

    window.addEventListener("credits:set", onSet);
    window.addEventListener("credits:spend", onSpend);
    window.addEventListener("credits:refresh", onRefresh);
    return () => {
      window.removeEventListener("credits:set", onSet);
      window.removeEventListener("credits:spend", onSpend);
      window.removeEventListener("credits:refresh", onRefresh);
    };
  }, [refresh, spend]);

  const value = React.useMemo(() => ({
    credits,
    loading,
    refresh,
    setCredits: (n) => {
      const v = Number(n ?? 0);
      if (Number.isFinite(v)) setCredits(v);
    },
    spend,
  }), [credits, loading, refresh, spend]);

  return <CreditsContext.Provider value={value}>{children}</CreditsContext.Provider>;
}

export function useCredits() {
  const ctx = React.useContext(CreditsContext);
  if (!ctx) throw new Error("useCredits must be used within <CreditsProvider>");
  return ctx;
}